import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import type { RobotProfileId } from "lib/robotProfileApi";
import { ROBOT_PROFILE_IMAGES } from "lib/consts/robotProfiles";

type Props = {
    profileId: RobotProfileId;
    /** Bridge/profile is selected (running) on the ARC. */
    isSelected: boolean;
    /** Robot link is up (`/state/connected`). */
    isConnected: boolean;
    disabled: boolean;
    connecting: boolean;
    onConnect: () => void;
    onDisconnect: () => void;
    onConfigure: () => void;
};

const RobotProfileCard: React.FC<Props> = ({
    profileId,
    isSelected,
    isConnected,
    disabled,
    connecting,
    onConnect,
    onDisconnect,
    onConfigure,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const imageSrc = ROBOT_PROFILE_IMAGES[profileId];
    const showAsCurrent = isSelected;
    const badgeLabel = isConnected
        ? t("robot.active")
        : t("robot.selected", { defaultValue: "Selected" });

    return (
        <article
            className={`relative flex flex-col overflow-hidden rounded-r10 border bg-black100 ${
                showAsCurrent ? "border-primary2 ring-2 ring-primary2/40" : "border-black80"
            }`}
            aria-label={t(`robot.options.${profileId}.title`)}
        >
            {showAsCurrent ? (
                <span
                    className={`absolute right-2 top-2 z-10 rounded-r5 border px-2 py-0.5 text-h5 font-semibold text-white ${
                        isConnected
                            ? "border-primary2 bg-primary2"
                            : "border-black60 bg-black80"
                    }`}
                >
                    {badgeLabel}
                </span>
            ) : null}

            <div className="flex h-40 items-center justify-center bg-black90 px-4 pt-4">
                {imageSrc ? (
                    <img
                        src={imageSrc}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                        draggable={false}
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center rounded-r5 border border-dashed border-black70">
                        <p className="px-3 text-center text-h5 text-black60">
                            {t("robot.imageSoon", { defaultValue: "Photo coming soon" })}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-3 p-3">
                <p className="text-h5 font-semibold text-white">
                    {t(`robot.options.${profileId}.title`)}
                </p>

                <div className="mt-auto flex flex-col gap-2">
                    {showAsCurrent ? (
                        <FunctionalButton
                            title={t("common:disconnect")}
                            type="button"
                            className="w-full rounded-r5 border border-black60 bg-black90 px-2 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                            disabled={disabled || connecting}
                            onClick={onDisconnect}
                        />
                    ) : (
                        <FunctionalButton
                            title={t("robot.connect")}
                            type="button"
                            className="w-full rounded-r5 border border-primary2 bg-primary2 px-2 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                            disabled={disabled || connecting}
                            onClick={onConnect}
                        />
                    )}
                    <FunctionalButton
                        title={t("robot.configure", { defaultValue: "Configure" })}
                        type="button"
                        className="w-full rounded-r5 border border-black60 bg-black90 px-2 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40 disabled:opacity-50"
                        disabled={disabled || connecting}
                        onClick={onConfigure}
                    />
                </div>
            </div>
        </article>
    );
};

export default RobotProfileCard;
